import React, { useEffect, useState } from "react";
import { useAuth } from "../../../../../provider/Auth.provider";
import Attendance from "../Attendance/Attendance";
import ButtonComponent from "../../../../../components/CssComponents/ButtonComponent/ButtonComponent";
import { notification, Modal, Input, Switch } from "antd";
import { IoChevronDown, IoChevronForward } from "react-icons/io5";

// import {
//   buildPackagesForTrainer,
//   getModuleMeta,
//   updateModuleMeta,
// } from "../../../../../service/trainerProgress.service";
import { getStudentsEnrolledInModule } from "../../../../../service/getStudentsEnrolledInCourse";
import {
  buildPackagesForTrainer,
  getModuleMeta,
  updateModuleMeta,
} from "../../../../../service/trainerProgress.service";

/* ---- palette helpers ---- */
const goldGrad =
  "bg-gradient-to-r from-[#B17E32] via-[#F3E681] to-[#CF9F41] text-black";

const LeftAccent = ({ tone = "gold" }) => (
  <span
    className={`absolute left-2 top-2 bottom-2 w-1 rounded-full ${
      tone === "gold"
        ? "bg-gradient-to-b from-[#F3E681] to-[#B17E32]"
        : "bg-neutral-700"
    }`}
  />
);

/* ============================
   Progress UI (Trainer View)
   ============================ */
const Progress = ({ data }) => {
  const { user } = useAuth();
  const trainerId = user?.uid;

  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(false);

  // attendance state
  const [showAttendance, setShowAttendance] = useState(false);
  const [studentsEnrolledInModule, setStudentsEnrolledInModule] = useState([]);
  const [singleModuleName, setSingleModuleName] = useState("");
  const [singleModuleId, setSingleModuleId] = useState("");
  const [singleCourseId, setSingleCourseId] = useState("");
  const [singleBatchId, setSingleBatchId] = useState("");
  const [singleSettingId, setSingleSettingId] = useState("");

  // collapsibles
  const [openPkg, setOpenPkg] = useState({}); // { [pkgIndex]: bool }
  const [openBatch, setOpenBatch] = useState({}); // { [`${pkgIndex}-${batchIndex}`]: bool }

  // edit module modal state
  const [editOpen, setEditOpen] = useState(false);
  const [editModuleId, setEditModuleId] = useState(null);
  const [editModuleName, setEditModuleName] = useState("");
  const [editLiveLink, setEditLiveLink] = useState("");
  const [editCompleted, setEditCompleted] = useState(false);
  const [editSaving, setEditSaving] = useState(false);
  useEffect(() => {
    console.log(packages, "getting packages");
  }, [packages]);
  /* --- load trainer-visible packages/batches/modules --- */
  useEffect(() => {
    const run = async () => {
      if (!trainerId) return;
      setLoading(true);
      try {
        const list = await buildPackagesForTrainer(data || [], trainerId);
        setPackages(list);
        // console.log(packages);
      } catch (e) {
        console.error(e);
        notification.error({
          message: "Failed to load data",
          description: "Could not load your assigned modules.",
        });
      } finally {
        setLoading(false);
      }
    };
    run();
  }, [data, trainerId]);

  /* --- open Attendance modal --- */
  const showAttendanceModal = async (
    pkgID,
    settingId,
    batchId,
    courseId,
    moduleId,
    moduleName
  ) => {
    setShowAttendance(true);
    setSingleModuleName(moduleName);
    setSingleModuleId(moduleId);
    setSingleCourseId(courseId);
    setSingleBatchId(batchId);
    setSingleSettingId(settingId);

    try {
      const students = await getStudentsEnrolledInModule({
        packageId: pkgID,
        courseId,
        batchId,
        settingId,
        moduleId,
        trainerId,
      });
      console.log("Students in module", students);
      setStudentsEnrolledInModule(students || []);
    } catch (error) {
      console.error("Failed to load students:", error);
      notification.error({
        message: "Error Loading Students",
        description: "Failed to load student data. Please try again.",
        placement: "topRight",
        duration: 3,
      });
    }
  };

  const closeAttendanceModal = () => {
    setShowAttendance(false);
    setStudentsEnrolledInModule([]);
    setSingleModuleName("");
    setSingleModuleId("");
    setSingleBatchId("");
    setSingleSettingId("");
  };

  /* --- open Edit Module modal and hydrate --- */
  const openEdit = async (moduleId, moduleName) => {
    setEditModuleId(moduleId);
    setEditModuleName(moduleName || "");
    setEditOpen(true);
    try {
      const meta = await getModuleMeta(moduleId);
      setEditLiveLink(meta?.liveLink || "");
      setEditCompleted(!!meta?.isCompleted);
    } catch (e) {
      // soft fail, allow editing anyway
      setEditLiveLink("");
      setEditCompleted(false);
    }
  };

  const onSaveEdit = async () => {
    if (!editModuleId) return;
    if (editLiveLink && !/^https?:\/\/.+/i.test(editLiveLink)) {
      notification.warning({
        message: "Live link must start with http:// or https://",
      });
      return;
    }
    setEditSaving(true);
    try {
      await updateModuleMeta({
        moduleId: editModuleId,
        liveLink: editLiveLink,
        isCompleted: editCompleted,
      });
      notification.success({ message: "Module updated" });
      setEditOpen(false);
    } catch (e) {
      console.error(e);
      notification.error({ message: "Failed to update module" });
    } finally {
      setEditSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0B0B0B] p-4 text-zinc-100">
      <div className="mx-auto max-w-6xl">
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <div className="h-8 w-8 animate-spin rounded-full border-4 border-zinc-800 border-t-[#CF9F41]" />
          </div>
        ) : packages.length === 0 ? (
          <p className="text-center text-zinc-400 text-base">
            No modules assigned to you yet.
          </p>
        ) : (
          <div className="space-y-4">
            {packages.map((pkg, pkgIndex) => {
              const pkgKeyOpen = !!openPkg[pkgIndex];
              const batchCount = pkg.batches?.length || 0;

              return (
                <section
                  key={pkgIndex}
                  className="relative rounded-2xl border border-white/10 bg-gradient-to-b from-[#0E0E0E] to-black shadow-[0_18px_50px_rgba(0,0,0,0.45)]"
                >
                  <LeftAccent tone="gold" />
                  {/* Package header */}
                  <button
                    onClick={() =>
                      setOpenPkg((p) => ({ ...p, [pkgIndex]: !p[pkgIndex] }))
                    }
                    className="w-full px-4 py-3 flex items-center justify-between gap-3"
                  >
                    <div className="flex items-center gap-3">
                      <span className="inline-flex h-6 w-6 items-center justify-center rounded-md border border-white/10 bg-black/40 text-zinc-400">
                        {pkgKeyOpen ? (
                          <IoChevronDown className="h-3.5 w-3.5" />
                        ) : (
                          <IoChevronForward className="h-3.5 w-3.5" />
                        )}
                      </span>
                      <div className="grid gap-0.5 text-left">
                        <h3 className="text-[15px] font-semibold tracking-tight">
                          {pkg.packageName || "Unnamed Package"}
                        </h3>
                        <div className="text-xs text-zinc-400">
                          {batchCount > 0
                            ? `${batchCount} ${
                                batchCount === 1 ? "batch" : "batches"
                              }`
                            : "No batches assigned"}
                        </div>
                      </div>
                    </div>
                    <span
                      className={`rounded-full px-2.5 py-1 text-[11px] font-bold ${goldGrad}`}
                    >
                      {pkgKeyOpen ? "Hide" : "View"}
                    </span>
                  </button>

                  {/* Package body */}
                  {pkgKeyOpen && batchCount > 0 && (
                    <div className="px-3 pb-3">
                      <div className="space-y-3">
                        {pkg.batches.map((batch, batchIndex) => {
                          const key = `${pkgIndex}-${batchIndex}`;
                          const isOpen = !!openBatch[key];

                          // courses visible to this trainer
                          const trainerCourseIds = Array.from(
                            new Set(
                              Object.values(batch.moduleSettings || {})
                                .map((s) => s?.courseId)
                                .filter(Boolean)
                            )
                          );
                          if (trainerCourseIds.length === 0) return null;

                          return (
                            <div
                              key={batchIndex}
                              className="relative rounded-xl border border-white/10 bg-[#0B0B0B]/80 overflow-hidden"
                            >
                              <span className="absolute left-3 top-3 bottom-3 w-[2px] rounded-full bg-gradient-to-b from-[#CF9F41] to-[#B17E32]" />
                              {/* Batch header */}
                              <button
                                onClick={() =>
                                  setOpenBatch((p) => ({
                                    ...p,
                                    [key]: !p[key],
                                  }))
                                }
                                className="w-full flex items-center justify-between gap-2 px-3 py-2.5"
                              >
                                <div className="flex items-center gap-3 pl-2">
                                  <span className="inline-flex h-6 w-6 items-center justify-center rounded-md border border-white/10 bg-black/40 text-zinc-400">
                                    {isOpen ? (
                                      <IoChevronDown className="h-3.5 w-3.5" />
                                    ) : (
                                      <IoChevronForward className="h-3.5 w-3.5" />
                                    )}
                                  </span>
                                  <div className="grid gap-0.5 text-left">
                                    <h4 className="text-[14px] font-semibold tracking-tight">
                                      {batch.batchName || "Unnamed Batch"}
                                    </h4>
                                    <div className="text-xs text-zinc-400">
                                      {trainerCourseIds.length}{" "}
                                      {trainerCourseIds.length === 1
                                        ? "course"
                                        : "courses"}
                                    </div>
                                  </div>
                                </div>
                                <span className="rounded-md border border-white/10 bg-black/40 px-2 py-0.5 text-[11px] text-zinc-300">
                                  {batch.batchStatus || "ongoing"}
                                </span>
                              </button>

                              {/* Batch body */}
                              {isOpen && (
                                <div className="px-3 pb-3">
                                  <div className="space-y-3">
                                    {trainerCourseIds.map((courseId) => (
                                      <div
                                        key={courseId}
                                        className="rounded-lg border border-white/10 bg-gradient-to-b from-black/60 to-[#0E0E0E] p-3"
                                      >
                                        <div className="flex items-center justify-between gap-2 border-b border-white/10 pb-2">
                                          <h5 className="text-[13px] font-semibold">
                                            Course ID:{" "}
                                            <span className="text-zinc-300">
                                              {courseId}
                                            </span>
                                          </h5>
                                          <span className="text-[11px] text-zinc-400">
                                            Your modules
                                          </span>
                                        </div>

                                        <div className="mt-2 space-y-2">
                                          {Object.entries(
                                            batch.moduleSettings || {}
                                          )
                                            .filter(
                                              ([, s]) =>
                                                s?.courseId === courseId &&
                                                s?.moduleTrainerId === trainerId
                                            )
                                            .map(([settingId, settings]) => (
                                              <div
                                                key={settingId}
                                                className="relative rounded-md border border-white/10 bg-[#0B0B0B] p-3 pl-4"
                                              >
                                                <span className="absolute left-2 top-2 bottom-2 w-0.5 rounded-full bg-gradient-to-b from-[#F3E681] to-[#B17E32]" />
                                                <div className="grid gap-2 pl-2">
                                                  <div className="flex items-center justify-between">
                                                    <p className="text-[13px] font-semibold">
                                                      {settings.moduleName ||
                                                        "N/A"}
                                                    </p>
                                                    <div className="flex items-center gap-2">
                                                      <ButtonComponent
                                                        onClick={() => {
                                                          const batchKey =
                                                            String(
                                                              batch.batchId ||
                                                                batch.id
                                                            );
                                                          showAttendanceModal(
                                                            pkg.id,
                                                            settingId,
                                                            batchKey,
                                                            courseId,
                                                            settings.moduleId,
                                                            settings.moduleName
                                                          );
                                                        }}
                                                        buttonText="Attendance"
                                                      />
                                                      <button
                                                        onClick={() =>
                                                          openEdit(
                                                            settings.moduleId,
                                                            settings.moduleName
                                                          )
                                                        }
                                                        className="rounded-md border border-white/15 bg-black/40 px-3 py-1.5 text-xs font-semibold text-zinc-200 hover:border-[#CF9F41] hover:text-[#CF9F41] transition"
                                                      >
                                                        Edit
                                                      </button>
                                                    </div>
                                                  </div>

                                                  <div className="flex flex-wrap gap-3 text-[12px] text-zinc-300">
                                                    {settings.moduleStartDate && (
                                                      <span className="opacity-80">
                                                        Start:{" "}
                                                        {new Date(
                                                          settings.moduleStartDate
                                                        ).toLocaleDateString()}
                                                      </span>
                                                    )}
                                                    {settings.moduleEndDate && (
                                                      <span className="opacity-80">
                                                        End:{" "}
                                                        {new Date(
                                                          settings.moduleEndDate
                                                        ).toLocaleDateString()}
                                                      </span>
                                                    )}
                                                  </div>
                                                </div>
                                              </div>
                                            ))}
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </section>
              );
            })}
          </div>
        )}
      </div>

      {/* Attendance Modal (your existing component) */}
      <Attendance
        showAttendanceModal={showAttendance}
        closeAttendanceModal={closeAttendanceModal}
        singleModuleName={singleModuleName}
        singleModuleId={singleModuleId}
        singleCourseId={singleCourseId}
        singleBatchId={singleBatchId}
        singleSettingId={singleSettingId}
        studentsEnrolledInModule={studentsEnrolledInModule}
        trainerId={trainerId}
      />

      {/* Edit Module Meta (modal kept IN THIS FILE = UI only) */}
      <Modal
        open={editOpen}
        onCancel={() => setEditOpen(false)}
        footer={null} /* we render a custom footer */
        title={
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span className="h-5 w-1 rounded-full bg-gradient-to-b from-[#F3E681] to-[#B17E32]" />
              <div className="leading-tight">
                <div className="text-base font-semibold text-white">
                  Edit Module
                </div>
                <div className="text-[11px] text-zinc-400">
                  Changes update{" "}
                  <code className="text-zinc-300">lms_module.liveLink</code>{" "}
                  &amp; <code className="text-zinc-300">isCompleted</code>
                </div>
              </div>
            </div>
            <span className="rounded-full border border-white/10 bg-black/40 px-2 py-0.5 text-[11px] text-zinc-300">
              {editModuleName || editModuleId}
            </span>
          </div>
        }
        styles={{
          body: { background: "#0B0B0B" },
          header: {
            background: "#0B0B0B",
            borderBottom: "1px solid rgba(255,255,255,0.08)",
          },
          footer: {
            background: "#0B0B0B",
            borderTop: "1px solid rgba(255,255,255,0.08)",
          },
        }}
      >
        {/* CARD */}
        <div className="rounded-xl border border-white/10 bg-gradient-to-b from-[#0E0E0E] to-black p-4">
          {/* live link field */}
          <label className="mb-1 block text-sm font-medium text-zinc-200">
            Live link
          </label>
          <div className="relative">
            <Input
              value={editLiveLink}
              onChange={(e) => setEditLiveLink(e.target.value)}
              placeholder="https://meet.google.com/… or Zoom link"
              allowClear
              className="!bg-[#0B0B0B] !text-zinc-100"
            />
            {/* actions on the right inside input row */}
            <div className="mt-2 flex items-center justify-between">
              <div className="text-[12px] text-zinc-400">
                Paste a shareable Meet/Zoom URL. Must start with{" "}
                <span className="text-zinc-200">http://</span> or{" "}
                <span className="text-zinc-200">https://</span>.
              </div>
              <div className="flex items-center gap-2">
                {editLiveLink ? (
                  <>
                    <a
                      href={editLiveLink}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="rounded-md border border-white/10 bg-black/40 px-2 py-1 text-[12px] text-zinc-200 hover:border-[#CF9F41] hover:text-[#CF9F41] transition"
                    >
                      Open
                    </a>
                    <button
                      onClick={() => {
                        navigator.clipboard.writeText(editLiveLink);
                        notification.success({ message: "Link copied" });
                      }}
                      className="rounded-md border border-white/10 bg-black/40 px-2 py-1 text-[12px] text-zinc-200 hover:border-[#CF9F41] hover:text-[#CF9F41] transition"
                    >
                      Copy
                    </button>
                  </>
                ) : null}
              </div>
            </div>
          </div>

          {/* divider */}
          <div className="my-4 h-px w-full bg-white/10" />

          {/* completed toggle */}
          <div className="flex items-center justify-between">
            <div>
              <div className="text-sm font-medium text-zinc-200">Status</div>
              <div className="text-[12px] text-zinc-400">
                Mark the module complete when you finish delivery.
              </div>
            </div>
            <div className="flex items-center gap-3">
              <span
                className={`rounded-full px-2.5 py-1 text-[11px] font-semibold ${
                  editCompleted
                    ? "bg-gradient-to-r from-[#B17E32] via-[#F3E681] to-[#CF9F41] text-black"
                    : "border border-white/10 bg-black/40 text-zinc-300"
                }`}
              >
                {editCompleted ? "Completed" : "Ongoing"}
              </span>
              <Switch
                checked={editCompleted}
                onChange={setEditCompleted}
                checkedChildren="✓"
                unCheckedChildren="–"
              />
            </div>
          </div>
        </div>

        {/* FOOTER */}
        <div className="mt-4 flex items-center justify-between">
          {/* left helper */}
          <div className="text-[12px] text-zinc-500">
            Pro tip: share the live link before class starts.
          </div>

          {/* actions */}
          <div className="flex items-center gap-2">
            <button
              onClick={() => setEditOpen(false)}
              className="rounded-md border border-white/10 bg-black/40 px-3 py-1.5 text-sm font-semibold text-zinc-200 hover:border-zinc-400/40 transition"
            >
              Close
            </button>
            <button
              onClick={onSaveEdit}
              disabled={!!editLiveLink && !/^https?:\/\/.+/i.test(editLiveLink)}
              className={`inline-flex items-center gap-2 rounded-md px-3 py-1.5 text-sm font-semibold shadow transition active:scale-[.99] ${
                !!editLiveLink && !/^https?:\/\/.+/i.test(editLiveLink)
                  ? "cursor-not-allowed opacity-60 bg-gradient-to-r from-[#B17E32] via-[#F3E681] to-[#CF9F41] text-black"
                  : "bg-gradient-to-r from-[#B17E32] via-[#F3E681] to-[#CF9F41] text-black hover:from-[#CF9F41] hover:to-[#B17E32]"
              }`}
            >
              {editSaving ? (
                <>
                  <span className="h-4 w-4 animate-spin rounded-full border-2 border-black/30 border-t-black" />
                  Saving…
                </>
              ) : (
                "Save"
              )}
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default Progress;
